import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import {
  Inventory2Outlined as InventoryIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useInventory } from '../../hooks/useInventory';

interface EmptyInventoryStateProps {
  title?: string;
  description?: string;
  showDialog?: boolean;
}

export const EmptyInventoryState: React.FC<EmptyInventoryStateProps> = ({
  title = 'Nenhum item encontrado',
  description = 'Comece adicionando seu primeiro item ao acervo',
  showDialog = true,
}) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { createItem } = useInventory();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    itemName: '',
    description: '',
    quantity: '1',
    category: '',
  });

  const handleQuickCreate = async () => {
    if (!formData.itemName.trim()) return;

    setLoading(true);
    try {
      const newItem = await createItem({
        itemName: formData.itemName,
        description: formData.description || undefined,
        quantity: parseInt(formData.quantity) || 1,
        category: formData.category || undefined,
      });

      setDialogOpen(false);
      navigate(`/inventory/${newItem.id}`);
    } catch (error: any) {
      console.error('Erro ao criar item:', error);
      alert(error.message || 'Erro ao criar item. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: 4,
          textAlign: 'center',
        }}
      >
        {/* Ícone grande */}
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: `${theme.colors.primary}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 3,
          }}
        >
          <InventoryIcon
            sx={{
              fontSize: '4rem',
              color: theme.colors.primary,
              opacity: 0.6,
            }}
          />
        </Box>

        {/* Título */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            color: theme.colors.text.primary,
            marginBottom: 1,
          }}
        >
          {title}
        </Typography>

        {/* Descrição */}
        <Typography
          variant="body1"
          sx={{
            color: theme.colors.text.secondary,
            marginBottom: 4,
            maxWidth: 400,
          }}
        >
          {description}
        </Typography>

        {/* Botões de ação */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {showDialog && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.text.contrast,
                borderRadius: theme.radii.pill,
                paddingX: 3,
                paddingY: 1.5,
                fontWeight: 'bold',
                textTransform: 'none',
                boxShadow: theme.shadows.medium,
                '&:hover': {
                  backgroundColor: theme.colors.primary,
                  opacity: 0.9,
                },
              }}
            >
              Cadastro Rápido
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={() => navigate('/inventory/new')}
            sx={{
              borderColor: theme.colors.primary,
              color: theme.colors.primary,
              borderRadius: theme.radii.pill,
              paddingX: 3,
              paddingY: 1.5,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                borderColor: theme.colors.primary,
                backgroundColor: `${theme.colors.primary}10`,
              },
            }}
          >
            Cadastro Completo
          </Button>
        </Box>
      </Box>
      {/* Dialog de Cadastro Rápido */}
      <Dialog
        open={dialogOpen}
        onClose={() => !loading && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: theme.radii.large,
              backgroundColor: theme.colors.background,
            },
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
            Cadastro Rápido
          </Typography>
          <IconButton
            onClick={() => setDialogOpen(false)}
            disabled={loading}
            sx={{ color: theme.colors.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ paddingTop: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Nome do Item *"
              value={formData.itemName}
              onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
              disabled={loading}
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.radii.medium,
                },
              }}
            />

            <TextField
              fullWidth
              label="Descrição"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              disabled={loading}
              multiline
              rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: theme.radii.medium,
                },
              }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Quantidade"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                disabled={loading}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: theme.radii.medium,
                  },
                }}
                slotProps={{
                  input: {
                    inputProps: { min: 1 },
                  }
                }}
              />

              <TextField
                label="Categoria"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                disabled={loading}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: theme.radii.medium,
                  },
                }}
              />
            </Box>

            <Typography
              variant="caption"
              sx={{ color: theme.colors.text.secondary, fontStyle: 'italic' }}
            >
               Você pode adicionar mais detalhes depois na página do item
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: 3, paddingTop: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={loading}
            sx={{
              color: theme.colors.text.secondary,
              textTransform: 'none',
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleQuickCreate}
            disabled={loading || !formData.itemName.trim()}
            variant="contained"
            sx={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.text.contrast,
              borderRadius: theme.radii.medium,
              paddingX: 3,
              textTransform: 'none',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: theme.colors.primary,
                opacity: 0.9,
              },
            }}
          >
            {loading ? 'Criando...' : 'Criar Item'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
